// LAYOUT-SPEC §0 — Deliverables (the program's deliverable list)
//
//   ONE QUESTION  What does this contract require us to deliver, and which of
//                 those need attention?
//   PRIMARY OBJECT  A collection of deliverables (Archetype B).
//   PRIMARY VERB    Open a deliverable. New deliverable is the one create.
//   WORST-CASE VOLUME  ~100 deliverables (OMEGA: 92). One page, client filters.
//
// Numbers per row come from ListOccurrences over the current contract period
// (the next date and its state); "needs a start date" from ListDuties'
// check codes, so the list never calls GetDuty per row.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraEntityCell, SpecraFormField, SpecraInput, SpecraListPage, SpecraPageHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ClipboardList } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DeliverableLookaheadList } from '../../../../components/delivery/widgets/DeliverableLookaheadList';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { EmptyState } from '../../_shared/components/PageStates';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner, AwaitingApprovalView } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { CmAwaitingApproval, CmStateTable, CmStatusBadge, CmTable } from '../components/common';
import { LogEventDrawer } from '../components/LogEventDrawer';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useCreateDuty, useOccurrences, useUpdateDuty } from '../hooks';
import { cmDate } from '../lib/format';
import { describeRule, loggableEvents } from '../lib/rule';
import { isOccurrenceState, isOpenState, occurrenceStatus, STATE_SHORT_LABEL, tierLabel } from '../lib/status';
import { useCmLookups } from '../lookups';
function nextOpen(occs) {
    const open = occs.filter((o)=>isOpenState(o.status?.state ?? '')).sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9'));
    return open[0] ?? [
        ...occs
    ].sort((a, b)=>(b.dueOn || '').localeCompare(a.dueOn || ''))[0];
}
/** Lowercase, trimmed, whitespace-collapsed, dash-variants unified — so
 * "CLIN 0002 — Help Desk Support" and "clin 0002  help desk support" compare
 * equal regardless of which dash or how much whitespace the source used. */ function normalizeForMatch(s) {
    return s.toLowerCase().replace(/[—–-]/g, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * A workstream/team name "relates to" a query if either contains the other.
 * The "query contains team name" direction is guarded to normalized names of
 * at least 3 characters, so a short team name (e.g. "IT") can't match nearly
 * any query by pure substring luck.
 */ function teamRelatesToQuery(teamName, q) {
    const normTeam = normalizeForMatch(teamName);
    const normQ = normalizeForMatch(q);
    if (!normTeam || !normQ) return false;
    if (normTeam.includes(normQ)) return true;
    return normTeam.length >= 3 && normQ.includes(normTeam);
}
/** ?awaiting=1 swaps the list for the proposals awaiting approval (D111). */ export function DeliverablesPage() {
    const { programId } = useProgramContext();
    const [sp] = useSearchParams();
    return sp.get('awaiting') ? /*#__PURE__*/ _jsx(AwaitingApprovalView, {
        programId: programId
    }) : /*#__PURE__*/ _jsx(DeliverablesList, {
        programId: programId
    });
}
function DeliverablesList({ programId }) {
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const [sp, setSp] = useSearchParams();
    const stateFilter = sp.get('state') ?? '';
    const periodFilter = sp.get('period') ?? '';
    const query = sp.get('q') ?? '';
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
    });
    const stateRows = useOccurrences(programId, periodFilter ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodFilter,
        states: [
            stateFilter
        ]
    } : {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        states: [
            stateFilter
        ]
    }, {
        enabled: !!stateFilter
    });
    const [team, setTeam] = useState('');
    const [attention, setAttention] = useState(false);
    const [editing, setEditing] = useState(null);
    const [logging, setLogging] = useState(false);
    const canLog = access.canWrite && loggableEvents(lk.rules, lk.duties).length > 0;
    const rows = useMemo(()=>{
        const byDuty = new Map();
        for (const o of occs.data?.occurrences ?? []){
            const arr = byDuty.get(o.dutyId) ?? [];
            arr.push(o);
            byDuty.set(o.dutyId, arr);
        }
        return lk.duties.map((duty)=>{
            const rules = (lk.rulesByDuty.get(duty.id) ?? []).filter((r)=>!r.assignmentId);
            const checks = lk.checkCodes.get(duty.id) ?? [];
            const list = byDuty.get(duty.id) ?? [];
            return {
                duty,
                ruleText: rules.length ? describeRule(rules[0]) + (rules.length > 1 ? ` (+${rules.length - 1} more)` : '') : '',
                needsStart: checks.includes('needs_start_date'),
                checks,
                next: nextOpen(list),
                teams: new Set(list.map((o)=>o.workstreamId))
            };
        }).sort((a, b)=>a.duty.title.localeCompare(b.duty.title));
    }, [
        lk.duties,
        lk.rulesByDuty,
        lk.checkCodes,
        occs.data
    ]);
    const q = query.trim().toLowerCase();
    const visible = rows.filter((r)=>{
        if (team && !r.teams.has(team === 'contract-wide' ? '' : team)) return false;
        if (attention) {
            const s = r.next?.status?.state ?? '';
            if (!(r.checks.length > 0 || s === 'late' || s === 'rejected' || !r.ruleText)) return false;
        }
        if (q) {
            const titleMatch = r.duty.title.toLowerCase().includes(q) || r.duty.pwsRef.toLowerCase().includes(q);
            // An imported duty carries no team link and its title never mentions
            // the workstream — the deliverable number (duty_key) is the only place
            // the workstream survives, e.g. "CAATS-5001AH-DEL-1-02".
            const keyMatch = r.duty.dutyKey.toLowerCase().includes(q);
            const teamMatch = [
                ...r.teams
            ].some((id)=>teamRelatesToQuery(lk.teamName(id), query));
            if (!titleMatch && !keyMatch && !teamMatch) return false;
        }
        return true;
    });
    const columns = [
        {
            id: 'title',
            header: 'Deliverable',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.duty.title,
                    description: r.duty.endedOn ? `Ended ${cmDate(r.duty.endedOn)}` : undefined
                })
        },
        {
            id: 'source',
            header: 'Source',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "text-muted-foreground",
                    children: [
                        r.duty.origin === 'sow' ? 'SOW' : r.duty.origin === 'manual' ? 'By hand' : 'Award',
                        r.duty.pwsRef ? ` PWS ${r.duty.pwsRef}` : '',
                        r.duty.letter ? ` ${r.duty.letter}` : ''
                    ]
                })
        },
        {
            id: 'rule',
            header: 'Rule',
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    "data-testid": Testids.cmDuties.ruleCell,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: r.ruleText ? '' : 'text-muted-foreground',
                            children: r.ruleText || 'No rule'
                        }),
                        r.needsStart ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "This rule has no first date, so no dates exist yet.",
                            children: /*#__PURE__*/ _jsx("span", {
                                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    "data-testid": Testids.cmDuties.needsStartBadge,
                                    children: "Needs a start date"
                                })
                            })
                        }) : null
                    ]
                })
        },
        {
            id: 'next',
            header: 'Next due',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: r.next ? cmDate(r.next.dueOn, '—') : '—'
                })
        },
        {
            id: 'state',
            header: 'State',
            cell: (r)=>r.next ? /*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.cmDuties.stateCell,
                    children: /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex flex-wrap items-center gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: occurrenceStatus(r.next, r.duty.finalReviewTier)
                            }),
                            /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                                programId: programId,
                                occurrenceId: r.next.id
                            })
                        ]
                    })
                }) : /*#__PURE__*/ _jsx(SpecraText, {
                    size: "caption",
                    tone: "muted",
                    "data-testid": Testids.cmDuties.stateCell,
                    children: r.ruleText ? 'No dates this period' : 'No dates'
                })
        }
    ];
    const empty = /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmDuties.empty,
        children: /*#__PURE__*/ _jsx(EmptyState, {
            icon: ClipboardList,
            title: "No deliverables yet",
            description: "The award’s deliverables appear here after intake; add one by hand now.",
            action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setEditing('new'),
                children: "New deliverable"
            }) : undefined
        })
    });
    const clearState = ()=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            n.delete('state');
            n.delete('period');
            return n;
        });
    const clearQuery = ()=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            n.delete('q');
            return n;
        });
    const noMatch = /*#__PURE__*/ _jsx(SpecraEmptyState, {
        framed: true,
        title: `No deliverables match “${query}”`,
        description: "Search looks at the title, PWS reference, deliverable number and team.",
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            variant: "ghost",
            onClick: clearQuery,
            "data-testid": Testids.cmDuties.noMatchClear,
            children: "Clear search"
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraListPage, {
                "data-testid": Testids.cmDuties.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: "Deliverables",
                            meta: lk.duties.length,
                            description: "What the contract requires, each with its rule and its next date.",
                            actions: /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    canLog ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setLogging(true),
                                        "data-testid": Testids.cmDuty.logEvent,
                                        children: "Log event"
                                    }) : null,
                                    access.canWrite && lk.duties.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>params.open('submit', 'new'),
                                        children: "+ Submit"
                                    }) : null,
                                    access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>setEditing('new'),
                                        "data-testid": Testids.cmDuties.create,
                                        children: "New deliverable"
                                    }) : null
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                            programId: programId
                        }),
                        !stateFilter && lk.duties.length > 0 ? /*#__PURE__*/ _jsx(DeliverableLookaheadList, {
                            programId: programId
                        }) : null,
                        stateFilter ? /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            isOccurrenceState(stateFilter) ? STATE_SHORT_LABEL[stateFilter] : stateFilter,
                                            periodFilter ? ` · ${lk.periodById.get(periodFilter)?.name ?? 'past period'}` : ' · this period',
                                            ' ',
                                            "· ",
                                            stateRows.data?.total ?? 0
                                        ]
                                    }),
                                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: clearState,
                                        children: "Clear"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(CmStateTable, {
                                    programId: programId,
                                    occurrences: stateRows.data?.occurrences ?? [],
                                    dutyTitle: (id)=>lk.dutyById.get(id)?.title,
                                    finalTierOf: (id)=>lk.dutyById.get(id)?.finalReviewTier,
                                    teamName: lk.teamName,
                                    onOpen: (id)=>params.open('occ', id),
                                    isLoading: stateRows.isLoading,
                                    empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                        size: "compact",
                                        title: "Nothing in this state.",
                                        description: "No date in this period is in it right now."
                                    })
                                })
                            ]
                        }) : null
                    ]
                }),
                toolbarProps: {
                    search: {
                        value: query,
                        onChange: (e)=>setSp((prev)=>{
                                const n = new URLSearchParams(prev);
                                if (e.target.value) n.set('q', e.target.value);
                                else n.delete('q');
                                return n;
                            }, {
                                replace: true
                            }),
                        onClear: clearQuery,
                        placeholder: 'Search deliverables…',
                        'data-testid': Testids.cmDuties.search
                    },
                    filters: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Team",
                                className: "w-56",
                                value: team,
                                onChange: (e)=>setTeam(e.target.value),
                                "data-testid": Testids.cmDuties.teamFilter,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "All teams"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "contract-wide",
                                        children: "Contract-wide"
                                    }),
                                    lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                            value: w.id,
                                            children: w.name
                                        }, w.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                label: "Show",
                                size: "sm",
                                value: attention ? 'attention' : 'all',
                                options: [
                                    {
                                        value: 'all',
                                        label: 'All'
                                    },
                                    {
                                        value: 'attention',
                                        label: 'Needs attention'
                                    }
                                ],
                                onValueChange: (v)=>setAttention(v === 'attention'),
                                testId: Testids.cmDuties.attentionFilter
                            })
                        ]
                    }),
                    resultSummary: visible.length === rows.length ? `${rows.length} total` : `${visible.length} of ${rows.length} shown`
                },
                children: /*#__PURE__*/ _jsx(CmTable, {
                    caption: "Deliverables",
                    rows: visible,
                    columns: columns,
                    rowKey: (r)=>r.duty.id,
                    rowTestId: Testids.cmDuties.row,
                    isLoading: lk.isLoading,
                    onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
                    getRowLabel: (r)=>r.duty.title,
                    empty: rows.length === 0 ? empty : query ? noMatch : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        framed: true,
                        title: "No deliverables match these filters",
                        description: "The team or attention filter hides every deliverable."
                    })
                })
            }),
            logging ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                onClose: ()=>setLogging(false)
            }) : null,
            editing ? /*#__PURE__*/ _jsx(DutyFormDrawer, {
                programId: programId,
                duty: editing === 'new' ? undefined : editing,
                onClose: ()=>setEditing(null),
                onSaved: (id)=>{
                    setEditing(null);
                    if (editing === 'new') navigate(`/delivery/programs/${programId}/deliverables/${id}`);
                }
            }) : null
        ]
    });
}
/**
 * A hand-added deliverable's identity key: its PWS pointer when stated, else
 * its title, plus a short random tail so two hand entries never collide. The
 * award's own deliverables get their CLIN-family key from intake (D107).
 */ export function manualDutyKey(pwsRef, letter, title) {
    const base = (pwsRef.trim() ? `pws-${pwsRef.trim()}${letter.trim() ? `-${letter.trim()}` : ''}` : title).toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
    return `manual:${base || 'deliverable'}:${Math.random().toString(36).slice(2, 8)}`;
}
/** New / edit a deliverable (its identity fields; its rules live on its page). */ export function DutyFormDrawer({ programId, duty, onClose, onSaved }) {
    const [title, setTitle] = useState(duty?.title ?? '');
    const [pwsRef, setPwsRef] = useState(duty?.pwsRef ?? '');
    const [letter, setLetter] = useState(duty?.letter ?? '');
    const [tier, setTier] = useState(duty?.finalReviewTier || 'va_pm');
    const [locator, setLocator] = useState(duty?.sourceLocator ?? '');
    const [eventKind, setEventKind] = useState(duty?.eventKind ?? '');
    const lk = useCmLookups(programId);
    // A kind another deliverable already dates is not offered: one per event.
    const events = loggableEvents(lk.rules, lk.duties).filter((e)=>e.kind === duty?.eventKind || !lk.duties.some((d)=>d.eventKind === e.kind));
    const create = useCreateDuty(programId, {
        success: 'Deliverable added',
        errors: 'inline'
    });
    const update = useUpdateDuty(programId, {
        success: 'Deliverable saved',
        errors: 'inline'
    });
    const busy = create.isPending || update.isPending;
    const error = create.error ?? update.error;
    const save = ()=>{
        const fields = {
            ...duty ?? {},
            title: title.trim(),
            pwsRef: pwsRef.trim(),
            letter: letter.trim(),
            finalReviewTier: tier,
            sourceLocator: locator.trim(),
            eventKind
        };
        if (duty) update.mutate({
            dutyId: duty.id,
            fields
        }, {
            onSuccess: ()=>onSaved(duty.id)
        });
        else create.mutate({
            fields,
            origin: 'manual',
            dutyKey: manualDutyKey(pwsRef, letter, title)
        }, {
            onSuccess: (r)=>r.duty && onSaved(r.duty.id)
        });
    };
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: duty ? 'Edit deliverable' : 'New deliverable',
        description: duty ? undefined : 'Add a deliverable the contract requires. Its rule comes next.',
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuties.createDrawer,
        footerProps: {
            confirmLabel: duty ? 'Save' : 'Add deliverable',
            onConfirm: save,
            confirmDisabled: !title.trim() || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuties.createSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    required: true,
                    hint: "As the contract names it.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmDuties.createTitle
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "PWS reference",
                            hint: "e.g. 5.4.7.1",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: pwsRef,
                                onChange: (e)=>setPwsRef(e.target.value),
                                "data-testid": Testids.cmDuties.createPwsRef
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Letter",
                            hint: "e.g. A",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: letter,
                                onChange: (e)=>setLetter(e.target.value)
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Final review",
                    hint: "The government review that approves it. A contractor review never counts.",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        value: tier,
                        onChange: (e)=>setTier(e.target.value),
                        "data-testid": Testids.cmDuties.createTier,
                        children: [
                            'va_approver',
                            'va_pm',
                            'va_cor'
                        ].map((t)=>/*#__PURE__*/ _jsx("option", {
                                value: t,
                                children: tierLabel(t)
                            }, t))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Where the contract says so",
                    hint: "e.g. “Award PWS 5.4.7.1, p.66”",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: locator,
                        onChange: (e)=>setLocator(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Dates an event",
                    hint: "e.g. “Technical kickoff meeting within 10 business days of award”: logging the meeting meets this date, and the deliverables due after the meeting are projected from it until then.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: eventKind,
                        onChange: (e)=>setEventKind(e.target.value),
                        "data-testid": Testids.cmDuties.createEventKind,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "No event"
                            }),
                            events.map((e)=>/*#__PURE__*/ _jsx("option", {
                                    value: e.kind,
                                    children: e.label
                                }, e.kind))
                        ]
                    })
                }),
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, 'Could not save')
                }) : null
            ]
        })
    });
}
